import { useEffect, useState, useCallback } from "react";
import { X, Zap, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const TRIGGERS = [
  { v: "task_created", l: "Cuando se crea una tarea" },
  { v: "status_changed", l: "Cuando cambia de estado" },
  { v: "task_assigned", l: "Cuando se asigna una tarea" },
];
const ACTIONS = [
  { v: "change_status", l: "Mover a estado…" },
  { v: "assign_user", l: "Asignar a…" },
  { v: "set_due_in_days", l: "Fijar vencimiento en X días" },
  { v: "notify_user", l: "Notificar a…" },
  { v: "webhook", l: "Llamar webhook (URL)" },
  { v: "slack", l: "Enviar a Slack/Discord/Teams (webhook)" },
];

export const AutomationsPanel = ({ board, users, onClose }) => {
  const [rules, setRules] = useState([]);
  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState("status_changed");
  const [toStatus, setToStatus] = useState("");
  const [actionType, setActionType] = useState("assign_user");
  const [aStatus, setAStatus] = useState("");
  const [aUser, setAUser] = useState("");
  const [aDays, setADays] = useState("3");
  const [aUrl, setAUrl] = useState("");

  const load = useCallback(() => api.get(`/boards/${board.id}/automations`).then((r) => setRules(r.data)).catch(() => {}), [board.id]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const action = { type: actionType };
    if (actionType === "change_status") action.status = aStatus;
    if (actionType === "assign_user" || actionType === "notify_user") action.user_id = aUser;
    if (actionType === "set_due_in_days") action.days = parseInt(aDays) || 0;
    if (actionType === "webhook" || actionType === "slack") action.url = aUrl;
    const condition = trigger === "status_changed" && toStatus ? { to_status: toStatus } : {};
    try {
      await api.post(`/boards/${board.id}/automations`, { name: name || "Regla", trigger, condition, action });
      setName(""); load();
      toast.success("Automatización creada");
    } catch { toast.error("Revisa los datos de la regla"); }
  };

  const toggle = async (r) => { await api.put(`/automations/${r.id}/toggle`); load(); };
  const remove = async (r) => { await api.delete(`/automations/${r.id}`); load(); };

  const colName = (id) => board.columns.find((c) => c.id === id)?.title || "?";
  const userName = (id) => users.find((u) => u.id === id)?.name || "?";
  const describe = (r) => {
    const t = TRIGGERS.find((x) => x.v === r.trigger)?.l || r.trigger;
    const cond = r.condition?.to_status ? ` a «${colName(r.condition.to_status)}»` : "";
    const a = r.action || {};
    const map = {
      change_status: `mover a «${colName(a.status)}»`,
      assign_user: `asignar a ${userName(a.user_id)}`,
      set_due_in_days: `vencimiento en ${a.days} días`,
      notify_user: `notificar a ${userName(a.user_id)}`,
      webhook: `webhook → ${a.url}`,
      slack: `mensaje → ${a.url}`,
    };
    return `${t}${cond} → ${map[a.type] || a.type}`;
  };

  const selCls = "w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-primary";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#121214] border border-white/10 rounded-2xl p-6 animate-fade-up max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="automations-panel">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-bold flex items-center gap-2"><Zap className="h-5 w-5 text-[#FFD600]" /> Automatizaciones</h2>
          <button onClick={onClose} data-testid="close-automations"><X className="h-5 w-5 text-zinc-500 hover:text-white" /></button>
        </div>

        <div className="space-y-2 mb-5">
          {rules.map((r) => (
            <div key={r.id} className="flex items-center gap-3 bg-[#0A0A0B] rounded-lg px-3 py-2.5" data-testid={`rule-${r.id}`}>
              <button onClick={() => toggle(r)} data-testid={`toggle-rule-${r.id}`}
                className={`h-5 w-9 rounded-full relative transition-colors shrink-0 ${r.enabled ? "bg-[#00E676]" : "bg-white/10"}`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${r.enabled ? "left-[18px]" : "left-0.5"}`} />
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{r.name}</p>
                <p className="text-xs text-zinc-500 truncate">{describe(r)}</p>
              </div>
              <button onClick={() => remove(r)} data-testid={`delete-rule-${r.id}`} className="text-zinc-600 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {rules.length === 0 && <p className="text-sm text-zinc-600">Sin reglas. Crea la primera automatización.</p>}
        </div>

        <div className="border-t border-white/10 pt-4 space-y-3">
          <input data-testid="rule-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre de la regla" className={selCls} />
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="rule-trigger-select" value={trigger} onChange={(e) => setTrigger(e.target.value)} className={selCls}>
              {TRIGGERS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
            {trigger === "status_changed" && (
              <select data-testid="rule-tostatus-select" value={toStatus} onChange={(e) => setToStatus(e.target.value)} className={selCls}>
                <option value="">A cualquier estado</option>
                {board.columns.map((c) => <option key={c.id} value={c.id}>a «{c.title}»</option>)}
              </select>
            )}
          </div>
          <select data-testid="rule-action-select" value={actionType} onChange={(e) => setActionType(e.target.value)} className={selCls}>
            {ACTIONS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
          </select>
          {actionType === "change_status" && (
            <select data-testid="rule-astatus-select" value={aStatus} onChange={(e) => setAStatus(e.target.value)} className={selCls}>
              <option value="">Elige estado…</option>
              {board.columns.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          )}
          {(actionType === "assign_user" || actionType === "notify_user") && (
            <select data-testid="rule-auser-select" value={aUser} onChange={(e) => setAUser(e.target.value)} className={selCls}>
              <option value="">Elige persona…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          {actionType === "set_due_in_days" && (
            <input data-testid="rule-days-input" type="number" min="0" value={aDays} onChange={(e) => setADays(e.target.value)} placeholder="Días" className={selCls} />
          )}
          {(actionType === "webhook" || actionType === "slack") && (
            <input data-testid="rule-url-input" value={aUrl} onChange={(e) => setAUrl(e.target.value)} placeholder="https://hooks.slack.com/… o cualquier URL" className={selCls} />
          )}
          <button onClick={create} data-testid="create-rule-button"
            className="w-full bg-primary hover:bg-[#FF5047] text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2">
            <Plus className="h-4 w-4" /> Crear regla
          </button>
        </div>
      </div>
    </div>
  );
};
